import * as React from "react";
import {
  Grid,
  Link,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from "@mui/material";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddCptCodeCharge from "./AddCptCodeCharge";
import Modal from "../modals/Modal";
import AddNdcCharge from "./AddNdcCharge";
import { formatMonetaryValue } from "../utils/MonetaryFormatHelper";
import { useFlash } from "../FlashContext";

const deleteCptCodeChargeModalContent = (
  <div className="modal-content">
    <p className="align-center">
      You are attempting to delete this CPT Code Charge. This action cannot be
      undone. Would you like to continue?
    </p>
  </div>
);

const deleteNdcChargeModalContent = (
  <div className="modal-content">
    <p className="align-center">
      You are attempting to delete this NDC Charge. This action cannot be
      undone. Would you like to continue?
    </p>
  </div>
);

const PricingTables: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [showAddCptCodeChargeModal, setShowAddCptCodeChargeModal] =
    React.useState<boolean>(false);
  const [showDeleteCptCodeChargeModal, setShowDeleteCptCodeChargeModal] =
    React.useState<boolean>(false);
  const [refreshCptCodeCharges, setRefreshCptCodeCharges] =
    React.useState(false);
  const [selectedCptCodeCharge, setSelectedCptCodeCharge] =
    React.useState<any>(null);
  const [cptCodeCharges, setCptCodeCharges] = React.useState([]);

  const [showAddNdcChargeModal, setShowAddNdcChargeModal] =
    React.useState<boolean>(false);
  const [showDeleteNdcChargeModal, setShowDeleteNdcChargeModal] =
    React.useState<boolean>(false);
  const [refreshNdcCharges, setRefreshNdcCharges] = React.useState(false);
  const [selectedNdcCharge, setSelectedNdcCharge] = React.useState<any>(null);
  const [ndcCharges, setNdcCharges] = React.useState([]);

  React.useEffect(() => {
    fetchCptCodeCharges();
  }, [refreshCptCodeCharges]);

  React.useEffect(() => {
    fetchNdcCharges();
  }, [refreshNdcCharges]);

  const fetchCptCodeCharges = () => {
    fetch("/cpt_code_charges", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setCptCodeCharges(result.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchNdcCharges = () => {
    fetch("/ndc_charges", {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setNdcCharges(result.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const openAddCptCodeChargeModal = () => {
    setShowAddCptCodeChargeModal(true);
  };

  const closeAddCptCodeChargeModal = () => {
    setSelectedCptCodeCharge(null);
    setShowAddCptCodeChargeModal(false);
  };

  const handleCptCodeChargeEdit = (cptCodeCharge) => {
    setSelectedCptCodeCharge(cptCodeCharge);
    openAddCptCodeChargeModal();
  };

  const openDeleteCptCodeChargeModal = (cptCodeCharge) => {
    setSelectedCptCodeCharge(cptCodeCharge);
    setShowDeleteCptCodeChargeModal(true);
  };

  const closeDeleteCptCodeChargeModal = () => {
    setSelectedCptCodeCharge(null);
    setShowDeleteCptCodeChargeModal(false);
  };

  const openAddNdcChargeModal = () => {
    setShowAddNdcChargeModal(true);
  };

  const closeAddNdcChargeModal = () => {
    setSelectedNdcCharge(null);
    setShowAddNdcChargeModal(false);
  };

  const handleNdcChargeEdit = (ndcCharge) => {
    setSelectedNdcCharge(ndcCharge);
    openAddNdcChargeModal();
  };

  const openDeleteNdcChargeModal = (ndcCharge) => {
    setSelectedNdcCharge(ndcCharge);
    setShowDeleteNdcChargeModal(true);
  };

  const closeDeleteNdcChargeModal = () => {
    setSelectedNdcCharge(null);
    setShowDeleteNdcChargeModal(false);
  };

  const deleteCptCodeCharge = async () => {
    try {
      const response = await fetch(
        `/cpt_code_charges/${selectedCptCodeCharge.id}`,
        {
          method: "DELETE",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "CPT Code Charge Deleted Successfully",
          text: "CPT Code Charge deleted successfully",
          type: "success",
        });
        fetchCptCodeCharges();
      } else {
        showToast({
          title: "Failed to Delete CPT Code Charge",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Delete CPT Code Charge",
        text: error.message ?? "Failed to delete CPT Code Charge",
        type: "error",
      });
    } finally {
      closeDeleteCptCodeChargeModal();
    }
  };

  const deleteNdcCharge = async () => {
    try {
      const response = await fetch(`/ndc_charges/${selectedNdcCharge.id}`, {
        method: "DELETE",
        headers: getHeaders(authenticationSetting.csrfToken),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "NDC Charge Deleted Successfully",
          text: "NDC Charge deleted successfully",
          type: "success",
        });
        fetchNdcCharges();
      } else {
        showToast({
          title: "Failed to Delete NDC Charge",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Delete NDC Charge",
        text: error.message ?? "Failed to delete NDC Charge",
        type: "error",
      });
    } finally {
      closeDeleteNdcChargeModal();
    }
  };

  return (
    <div className="main-content-outer">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px" }}
      >
        <Grid
          item
          xs={12}
          className="patient-edit-container patient-edit-form"
          sx={{ paddingBottom: "0px" }}
        >
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={4}>
                <Stack direction="row" columnGap="12px" alignItems="center">
                  <Link
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={`/claim-builder`}
                  >
                    <ArrowBackIosNewIcon
                      style={{
                        borderRadius: "3px",
                        padding: "8px",
                        color: "black",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    />
                  </Link>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Service Charge Table
                  </p>
                </Stack>
              </Grid>

              <Grid item xs={6} className="q-btn-container">
                <Link
                  onClick={openAddCptCodeChargeModal}
                  className="grey-font"
                  style={{
                    height: "40px",
                    marginRight: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    position: "relative",
                    top: "-2px",
                  }}
                >
                  <img
                    src="/assets/menu-track.png"
                    width="20"
                    height="20"
                    alt="Add New Program"
                    style={{ padding: "5px" }}
                  />
                  Add Service Charge
                </Link>
              </Grid>
            </Grid>
            <Grid container>
              <Grid
                item
                xs={12}
                className="form-container"
                sx={{ paddingBottom: "0px !important" }}
              >
                {cptCodeCharges?.length === 0 ? (
                  <p
                    style={{
                      color: "#1E1E1E",
                      textAlign: "center",
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }}
                  >
                    You have not created a CPT Code Charge yet. You can create
                    your first CPT Code Charge{" "}
                    <span
                      style={{
                        color: "#3F51B5",
                        textDecorationLine: "underline",
                        cursor: "pointer",
                      }}
                      onClick={openAddCptCodeChargeModal}
                    >
                      here!
                    </span>
                  </p>
                ) : (
                  <TableContainer style={{ maxHeight: "calc(80vh - 200px)" }}>
                    <Table stickyHeader aria-label="a dense table">
                      <TableHead
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            "& td, & th": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell align="left" sx={{ width: "250px" }}>
                            CPT Code
                          </TableCell>
                          <TableCell align="left">
                            CPT Code Description
                          </TableCell>
                          <TableCell align="left">Charge Amount</TableCell>
                          <TableCell align="center" sx={{ width: "67px" }}>
                            Edit
                          </TableCell>
                          <TableCell align="center" sx={{ width: "53px" }}>
                            Remove
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cptCodeCharges?.map((cptCodeCharge) => (
                          <TableRow
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                            key={cptCodeCharge.id}
                          >
                            <TableCell align="left">
                              {cptCodeCharge.cpt_code}
                            </TableCell>
                            <TableCell align="left">
                              {cptCodeCharge.cpt_code_description}
                            </TableCell>
                            <TableCell align="left">
                              {`$${formatMonetaryValue(cptCodeCharge.charge_amount)}`}
                            </TableCell>
                            <TableCell align="center">
                              <CreateIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleCptCodeChargeEdit(cptCodeCharge)
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <DeleteIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  openDeleteCptCodeChargeModal(cptCodeCharge)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px", marginTop: "24px" }}
      >
        <Grid
          item
          xs={12}
          className="patient-edit-container patient-edit-form"
          sx={{ paddingBottom: "0px" }}
        >
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={4}>
                <p className="secondary-label" style={{ marginLeft: "0px" }}>
                  NDC Charge Table
                </p>
              </Grid>

              <Grid item xs={6} className="q-btn-container">
                <Link
                  onClick={openAddNdcChargeModal}
                  className="grey-font"
                  style={{
                    height: "40px",
                    marginRight: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    position: "relative",
                    top: "-2px",
                  }}
                >
                  <img
                    src="/assets/menu-track.png"
                    width="20"
                    height="20"
                    alt="Add New Program"
                    style={{ padding: "5px" }}
                  />
                  Add NDC Charge
                </Link>
              </Grid>
            </Grid>
            <Grid container>
              <Grid
                item
                xs={12}
                className="form-container"
                sx={{ paddingBottom: "0px !important" }}
              >
                {ndcCharges?.length === 0 ? (
                  <p
                    style={{
                      color: "#1E1E1E",
                      textAlign: "center",
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }}
                  >
                    You have not created an NDC Charge yet. You can create your
                    first NDC Charge{" "}
                    <span
                      style={{
                        color: "#3F51B5",
                        textDecorationLine: "underline",
                        cursor: "pointer",
                      }}
                      onClick={openAddNdcChargeModal}
                    >
                      here!
                    </span>
                  </p>
                ) : (
                  <TableContainer style={{ maxHeight: "calc(80vh - 200px)" }}>
                    <Table stickyHeader aria-label="a dense table">
                      <TableHead
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            "& td, & th": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell align="left" sx={{ width: "250px" }}>
                            Drug Code
                          </TableCell>
                          <TableCell align="left">
                            Drug Code Description
                          </TableCell>
                          <TableCell align="left">Charge Amount</TableCell>
                          <TableCell align="center" sx={{ width: "67px" }}>
                            Edit
                          </TableCell>
                          <TableCell align="center" sx={{ width: "53px" }}>
                            Remove
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ndcCharges?.map((ndcCharge) => (
                          <TableRow
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                            key={ndcCharge.id}
                          >
                            <TableCell align="left">
                              {ndcCharge.drug_code}
                            </TableCell>
                            <TableCell align="left">
                              {ndcCharge.drug_code_description}
                            </TableCell>
                            <TableCell align="left">
                              {`$${formatMonetaryValue(ndcCharge.charge_amount)}`}
                            </TableCell>
                            <TableCell align="center">
                              <CreateIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() => handleNdcChargeEdit(ndcCharge)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <DeleteIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  openDeleteNdcChargeModal(ndcCharge)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Modal
        successModalOpen={showDeleteCptCodeChargeModal}
        setSuccessModalOpen={closeDeleteCptCodeChargeModal}
        successHeader="Delete CPT Code Charge"
        successContent={deleteCptCodeChargeModalContent}
        successCallback={deleteCptCodeCharge}
        closeCallback={closeDeleteCptCodeChargeModal}
        confirmButtonText="Continue"
      />

      <Modal
        successModalOpen={showDeleteNdcChargeModal}
        setSuccessModalOpen={closeDeleteNdcChargeModal}
        successHeader="Delete NDC Charge"
        successContent={deleteNdcChargeModalContent}
        successCallback={deleteNdcCharge}
        closeCallback={closeDeleteNdcChargeModal}
        confirmButtonText="Continue"
      />

      {showAddCptCodeChargeModal && (
        <AddCptCodeCharge
          showAddCptCodeChargeModal={showAddCptCodeChargeModal}
          closeAddCptCodeChargeModal={closeAddCptCodeChargeModal}
          selectedCptCodeCharge={selectedCptCodeCharge}
          setRefreshCptCodeCharges={setRefreshCptCodeCharges}
        />
      )}

      {showAddNdcChargeModal && (
        <AddNdcCharge
          showAddNdcChargeModal={showAddNdcChargeModal}
          closeAddNdcChargeModal={closeAddNdcChargeModal}
          selectedNdcCharge={selectedNdcCharge}
          setRefreshNdcCharges={setRefreshNdcCharges}
        />
      )}
    </div>
  );
};

export default PricingTables;
